import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Activity, Bell, ChevronRight, ClipboardCheck, FileBarChart, LayoutDashboard, Menu, Search, Settings, Users, X } from 'lucide-react';
import { BrandLockup, Button, Chip } from '@fintracks/ui';
import { useAuth } from './AuthContext';
import { useClient } from '../data/hooks';
import { appMode } from '../data/repository';

const navigation = [
  { to: '/', label: 'Visão geral', icon: LayoutDashboard, end: true },
  { to: '/clientes', label: 'Clientes', icon: Users },
  { to: '/relatorios', label: 'Relatórios', icon: FileBarChart },
  { to: '/acompanhamento', label: 'Acompanhamento', icon: Activity },
  { to: '/revisao', label: 'Revisão de dados', icon: ClipboardCheck },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
];

const pageNames: Record<string, string> = {
  clientes: 'Clientes', plano: 'Plano financeiro', cenarios: 'Cenário', relatorios: 'Relatórios',
  acompanhamento: 'Acompanhamento', revisao: 'Revisão de dados', configuracoes: 'Configurações', novo: 'Novo cliente',
};

export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const clientId = pathParts[0] === 'clientes' && pathParts[1] !== 'novo' ? pathParts[1] ?? '' : '';
  const clientQuery = useClient(clientId);
  const crumbs = pathParts.map((part) => pageNames[part] ?? (part === clientId ? clientQuery.data?.fullName ?? 'Cliente' : part));
  const displayName = session && 'name' in session ? String(session.name) : session?.firstName || session?.email || 'Usuário';
  const initials = displayName.split(/\s|@/).filter(Boolean).map(part => part[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Pular para o conteúdo</a>
      <aside className={`sidebar ${menuOpen ? 'sidebar--open' : ''}`} aria-label="Navegação principal">
        <div className="sidebar__brand"><BrandLockup size={24} /><button className="icon-button sidebar__close" aria-label="Fechar menu" onClick={() => setMenuOpen(false)}><X size={20}/></button></div>
        <div className="workspace-switch"><span className="avatar avatar--company">A</span><span><strong>Fintracks</strong><small>Planejamento interno</small></span><ChevronRight size={16}/></div>
        <nav className="nav-list">
          <p className="nav-label">Workspace</p>
          {navigation.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} {...(end ? { end: true } : {})} onClick={() => setMenuOpen(false)} className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}><Icon size={18}/><span>{label}</span>{label === 'Acompanhamento' && <i>3</i>}</NavLink>)}
        </nav>
        <div className="sidebar__status"><Chip dotColor="var(--fintracks-positive)">Ambiente {appMode === 'demo' ? 'demo' : appMode === 'pocketbase' ? 'PocketBase' : 'Directus'}</Chip><p>{appMode === 'demo' ? 'Dados locais e sintéticos' : 'Dados canônicos do workspace'}</p></div>
        <button className="profile-card" onClick={() => { void logout(); navigate('/login'); }} aria-label="Sair da conta">
          <span className="avatar">{initials}</span><span><strong>{displayName}</strong><small>{session?.role?.name || 'Planejador'} · Sair</small></span><ChevronRight size={16}/>
        </button>
      </aside>
      {menuOpen && <button className="sidebar-backdrop" aria-label="Fechar menu" onClick={() => setMenuOpen(false)} />}
      <div className="shell-body">
        <header className="topbar">
          <button className="icon-button mobile-menu" aria-label="Abrir menu" onClick={() => setMenuOpen(true)}><Menu size={20}/></button>
          <div className="breadcrumbs"><span>Fintracks</span>{crumbs.map((crumb, i) => <span key={`${crumb}-${i}`}><ChevronRight size={13}/>{crumb}</span>)}</div>
          <div className="topbar__actions">
            <button className="search-trigger" onClick={() => navigate('/clientes')}><Search size={17}/><span>Buscar cliente</span><kbd>⌘ K</kbd></button>
            <button className="icon-button notification" aria-label="Notificações"><Bell size={18}/><i/></button>
            <Button variant="primary" onClick={() => navigate('/clientes/novo')}>Novo cliente</Button>
          </div>
        </header>
        <main id="main-content" className="main-content" tabIndex={-1}><Outlet /></main>
      </div>
    </div>
  );
}
